// components/DataMigrationAlert.js - 데이터 마이그레이션 알림 컴포넌트
import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const DataMigrationAlert = ({ onClose, onRetry }) => {
    const [migrationStatus, setMigrationStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        checkMigrationStatus();
    }, []);

    const checkMigrationStatus = async () => {
        try {
            const { idbGetSetting } = await import('../utils/idb');
            const status = {
                customersMigrated: await idbGetSetting('customers_migrated'),
                historiesMigrated: await idbGetSetting('histories_migrated'),
                hasLocalStorageCustomers: !!localStorage.getItem('customerData'),
                hasLocalStorageHistories: !!localStorage.getItem('customerHistory')
            };
            setMigrationStatus(status);
        } catch (e) {
            console.error('마이그레이션 상태 확인 실패:', e);
        }
    };


    const handleRetryMigration = async () => {
        setIsLoading(true);
        try {
            const { migrateAllLocalStorageData } = await import('../utils/idb');
            await migrateAllLocalStorageData();
            alert('마이그레이션이 완료되었습니다. 페이지를 새로고침해주세요.');
            window.location.reload();
        } catch (e) {
            console.error('마이그레이션 재시도 실패:', e);
            alert('마이그레이션에 실패했습니다. 고객센터에 문의해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDataValidation = async () => {
        setIsLoading(true);
        try {
            const { validateDataIntegrity } = await import('../utils/idb');
            const result = await validateDataIntegrity();

            if (result.success) {
                alert('데이터가 정상입니다.');
            } else {
                const message = `데이터 무결성 문제가 발견되었습니다.\n\n` +
                    `- 총 고객: ${result.totalCustomers}명\n` +
                    `- 총 히스토리: ${result.totalHistories}개\n` +
                    `- 문제: ${result.summary.totalIssues}개\n\n` +
                    `데이터 수리를 시도하시겠습니까?`;

                if (window.confirm(message)) {
                    const { repairDataIntegrity } = await import('../utils/idb');
                    const repairResult = await repairDataIntegrity();
                    alert(`데이터 수리가 완료되었습니다.\n\n` +
                        `제거된 고객: ${repairResult.removed.customers}명\n` +
                        `제거된 히스토리: ${repairResult.removed.histories}개`);
                }
            }
        } catch (e) {
            console.error('데이터 검증 실패:', e);
            alert('데이터 검증에 실패했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!migrationStatus) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                    <div className="flex items-center gap-3 mb-4">
                        <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
                        <h3 className="text-lg font-bold text-gray-800">상태 확인 중...</h3>
                    </div>
                </div>
            </div>
        );
    }

    const needsMigration = (migrationStatus.hasLocalStorageCustomers || migrationStatus.hasLocalStorageHistories) &&
        (!migrationStatus.customersMigrated || !migrationStatus.historiesMigrated);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
                <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                    <h3 className="text-lg font-bold text-gray-800">데이터 마이그레이션 알림</h3>
                </div>

                <div className="space-y-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2">현재 상태</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                {migrationStatus.customersMigrated ?
                                    <CheckCircle className="w-4 h-4 text-green-500" /> :
                                    <XCircle className="w-4 h-4 text-red-500" />
                                }
                                <span>고객 데이터 마이그레이션: {migrationStatus.customersMigrated ? '완료' : '미완료'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {migrationStatus.historiesMigrated ?
                                    <CheckCircle className="w-4 h-4 text-green-500" /> :
                                    <XCircle className="w-4 h-4 text-red-500" />
                                }
                                <span>히스토리 데이터 마이그레이션: {migrationStatus.historiesMigrated ? '완료' : '미완료'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {migrationStatus.hasLocalStorageCustomers ?
                                    <AlertTriangle className="w-4 h-4 text-orange-500" /> :
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                }
                                <span>localStorage 고객 데이터: {migrationStatus.hasLocalStorageCustomers ? '존재' : '없음'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {migrationStatus.hasLocalStorageHistories ?
                                    <AlertTriangle className="w-4 h-4 text-orange-500" /> :
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                }
                                <span>localStorage 히스토리 데이터: {migrationStatus.hasLocalStorageHistories ? '존재' : '없음'}</span>
                            </div>
                        </div>
                    </div>

                    {needsMigration && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <h4 className="font-semibold text-orange-800 mb-2">⚠️ 마이그레이션 필요</h4>
                            <p className="text-sm text-orange-700">
                                localStorage에 데이터가 있지만 IndexedDB로 마이그레이션이 완료되지 않았습니다.
                                데이터 손실을 방지하기 위해 마이그레이션을 진행해주세요.
                            </p>
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    {needsMigration && (
                        <button
                            onClick={handleRetryMigration}
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            마이그레이션 재시도
                        </button>
                    )}


                    <button
                        onClick={handleDataValidation}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <CheckCircle className="w-4 h-4" />
                        데이터 무결성 검증
                    </button>

                    <button
                        onClick={onClose}
                        className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
                    >
                        닫기
                    </button>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                    문제가 지속되면 고객센터에 문의해주세요.
                </div>
            </div>
        </div>
    );
};

export default DataMigrationAlert;
